import classNames from 'classnames'
import React, { useCallback, useRef, useState } from 'react'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { Breakpoints } from '../utilities/breakpoints'
import { isNullOrWhiteSpace } from '../utilities/string'
import { Grid } from './Grid'
import { ImageProps } from './Image'
import { MaximizeButton } from './MaximizeButton'
import { PauseButton } from './PauseButton'
import { SoundButton } from './SoundButton'
import { VimeoVideo } from './VimeoVideo'

export interface VideoHeroProps {
  previewImage?: ImageProps
  preamble?: string
  title?: string
  videoUrl?: string
  videoUrlMobile?: string
}

export const VideoHero = ({
  previewImage,
  preamble,
  title,
  videoUrl: videoUrlTb,
  videoUrlMobile,
}: VideoHeroProps) => {
  const [videoUrl, setVideoUrl] = useState<string | undefined>(
    videoUrlMobile ?? videoUrlTb
  )
  const [started, setStarted] = useState(false)
  const [paused, setPaused] = useState(false)
  const [soundEnabled, setSoundEnabled] = useState(false)
  const [fullscreenEnabled, setFullscreenEnabled] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useBreakpoint(Breakpoints.tb, above => {
    setVideoUrl(above ? videoUrlTb : videoUrlMobile ?? videoUrlTb)
    setSoundEnabled(false)
    setPaused(false)
  })

  const onPause = useCallback(() => {
    setPaused(prev => !prev)
  }, [])

  const onToggleFullscreen = useCallback(async () => {
    if (fullscreenEnabled) {
      await document.exitFullscreen()
      setFullscreenEnabled(false)
    } else {
      if (containerRef.current) {
        await containerRef.current.requestFullscreen()
        setFullscreenEnabled(true)
      }
    }
  }, [fullscreenEnabled])

  const applyMobileAspectRatio =
    !isNullOrWhiteSpace(videoUrlMobile) ||
    (isNullOrWhiteSpace(videoUrl) && !!previewImage)

  return (
    <div className="relative flex flex-col md:flex-row md:justify-end">
      {(!isNullOrWhiteSpace(title) || !isNullOrWhiteSpace(preamble)) && (
        <Grid
          additionalClassName={classNames(
            'py-45 gap-y-30 bg-senary-darkest',
            'sm:py-60',
            'md:absolute md:inset-0 md:py-0'
          )}
        >
          {!isNullOrWhiteSpace(title) && (
            <div
              className={classNames(
                'col-span-full',
                'tb:col-start-2 tb:col-span-2',
                'md:col-start-1 md:col-span-2',
                'lg:col-start-2 lg:col-span-2 lg:-ml-75 lg:-mr-45',
                'xxl:col-start-3 xxl:col-span-2'
              )}
            >
              <h1
                className={classNames(
                  'font-hero-title text-hero-title text-neutral text-center',
                  'sm:text-right',
                  'md:text-left'
                )}
              >
                {title}
              </h1>
            </div>
          )}

          {!isNullOrWhiteSpace(preamble) && (
            <div
              className={classNames(
                'col-span-full',
                'tb:col-start-2 tb:col-span-2',
                'sm:col-start-4 sm:col-span-2',
                'md:col-start-1 md:col-span-2',
                'lg:col-start-2 lg:col-span-2 lg:-ml-75 lg:-mr-45',
                'xxl:col-start-3 xxl:col-span-2'
              )}
            >
              <p className="font-t text-t-400 text-neutral lg:text-t-600">{preamble}</p>
            </div>
          )}
        </Grid>
      )}

      {(!isNullOrWhiteSpace(videoUrl) || !!previewImage) && (
        <div
          className={classNames(
            'relative w-full bg-senary-darkest',
            {
              'aspect-[4/5] tb:aspect-wide': applyMobileAspectRatio,
              'aspect-wide': !applyMobileAspectRatio,
            },
            'md:basis-1/2'
          )}
          ref={containerRef}
        >
          <VimeoVideo
            imgElementClassName="h-full w-full object-cover"
            loading="eager"
            muted={!soundEnabled}
            onBackgroundVideoStarted={() => setStarted(true)}
            paused={paused}
            previewImage={previewImage}
            title=""
            videoUrl={videoUrl}
          />

          {started && (
            <div
              className={classNames(
                'absolute flex gap-15 z-10 right-30 bottom-30',
                'lg:bottom-60 lg:right-90'
              )}
            >
              <PauseButton onClick={onPause} />

              <SoundButton
                active={soundEnabled}
                onClick={() => setSoundEnabled(old => !old)}
              />

              <MaximizeButton onClick={onToggleFullscreen} />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
